import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The Emergent Google auth callback delivers session_id
// via URL fragment (`#session_id=...`). We exchange it once, on mount, and
// then route the user into the app.

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  // useRef (not useState) so StrictMode double-invocation can't fire twice.
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    if (!sessionId) {
      setError("No session_id in callback URL");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the hash so a back-nav or refresh doesn't retry an old id.
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        const role = data?.user?.role;
        if (role === "driver") navigate("/driver", { replace: true });
        else navigate("/admin", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Google sign-in failed");
      }
    })();
  }, [location.hash, refresh, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-brand-surface px-6"
         data-testid="auth-callback">
      <Logo variant="full" size="md" />
      {error ? (
        <div className="max-w-md text-center">
          <div className="text-red-700 font-semibold">Sign-in failed</div>
          <div className="mt-2 text-sm text-brand-text-muted">{error}</div>
          <button
            onClick={() => navigate("/login", { replace: true })}
            data-testid="auth-callback-return"
            className="mt-4 text-sm text-brand-navy hover:underline"
          >
            ← Back to sign-in
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-brand-text-muted">
          <Loader2 className="w-4 h-4 animate-spin" /> Completing sign-in&hellip;
        </div>
      )}
    </div>
  );
};

export default AuthCallback;
